import React, { ReactElement } from 'react';
import { get, flow, isPlainObject, curry, curryRight } from 'lodash';
import Linkify from 'linkify-react';
import { VariableToken } from './tokenizer';
import { Paragraph, ParagraphItem } from './parser';
import tokenize from '~/utils/templating/tokenizer';
import parse from '~/utils/templating/parser';

type DataItemValue = string | number | ReactElement | null | undefined;

type DataItemObject = {
  type?: string;
  value?: DataItemValue;
};

export type DataItem = DataItemValue | DataItemObject;

export interface InputData {
  [x: string]: DataItem;
}

function getVariableData(item: VariableToken, data: InputData): DataItemObject {
  const value = get(data, item.data?.variableName as string);

  return (
    isPlainObject(value)
      ? value
      : {
          value,
        }
  ) as DataItemObject;
}

function defaultVariableTypeHandler(item: VariableToken, data: DataItemObject) {
  return data.value ?? item.data?.defaultValue;
}

function urlHandler(href: string) {
  return <a href={href}>{href}</a>;
}

function elementHandler(element: ReactElement) {
  return <React.Fragment>{element}</React.Fragment>;
}

export type VariableTypeHandler = typeof defaultVariableTypeHandler;

function variable(
  item: VariableToken,
  data: InputData,
  variableTypeHandlers: Record<string, VariableTypeHandler>
) {
  const variableData = getVariableData(item, data);
  const handler = get(
    variableTypeHandlers,
    variableData.type as string,
    defaultVariableTypeHandler
  );

  return handler(item, variableData);
}

function renderItem(
  item: ParagraphItem,
  data: InputData,
  variableTypeHandlers?: Record<string, VariableTypeHandler>
): string | number | ReactElement | undefined {
  switch (item.type) {
    case 'variable':
      return variable(item, data, variableTypeHandlers ?? {});
  }

  return item.data;
}

export interface Template {
  paragraphs: Paragraph[];
}

export function toString(template: Template, data: InputData) {
  return template.paragraphs
    .map((paragraph) => {
      return paragraph.items.map((item) => renderItem(item, data)).join('');
    })
    .join(' ');
}

const reactVariableTypeHandlers = {
  url: flow(defaultVariableTypeHandler, urlHandler),
  element: flow(defaultVariableTypeHandler, elementHandler),
};

export const buildTemplateProgram = flow(tokenize, parse);

export const templateProgramToString = curryRight(toString);

export const templateProgramToReact = curry(
  (data: InputData, template: Template) => {
    return <ReactTemplate template={template} data={data} />;
  }
);

export const createSanitizeComponent = (text: string) => {
  return flow(buildTemplateProgram, templateProgramToReact({}))(text);
};

export interface ReactTemplateProps {
  template: Template;
  data: InputData;
}

export const ReactTemplate = ({ template, data }: ReactTemplateProps) => (
  <React.Fragment>
    {template.paragraphs.map((paragraph, i) => (
      <p key={`paragraph_${i}`}>
        {paragraph.items.map((item, j) => (
          <Linkify
            key={`paragraph_${i}_${j}`}
            options={{
              target: {
                url: '_blank',
              },
            }}
          >
            {renderItem(item, data, reactVariableTypeHandlers)}
          </Linkify>
        ))}
      </p>
    ))}
  </React.Fragment>
);
